import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { RecommendationCategory } from '@lizrd/contracts';
import { getRecommendations } from '../../lib/api';
import { Card } from '../ui/card';
import { Select } from '../ui/select';
import { SkeletonText } from '../ui/skeleton';
import { InfoButton } from '../ui/info-button';
import { usd } from '../../lib/graph-visuals';
import { isRecoverable } from '../../lib/savings';
import { RECO_CATEGORY_META } from '../../lib/reco-categories';
import { CategoryIcon } from '../CategoryIcon';
import { useOrgDomainRecs, type EnvRecs } from './useOrgDomainRecs';

/** One recommendation, flattened for the drill-down (its domain rides along so the
 * row can link straight to the detail page). */
interface RecLite {
  id: string;
  domainId: string;
  title: string;
  /** The resource the recommendation acts on — disambiguates same-sounding titles. */
  resource: string;
  amount: number;
}
interface DomainGroup {
  domainId: string;
  name: string;
  amount: number;
  count: number;
  recs: RecLite[];
}
interface CategoryGroup {
  category: RecommendationCategory;
  amount: number;
  count: number;
  domains: DomainGroup[];
}

/**
 * Recoverable savings grouped by category, then (org scope) by domain, then the
 * individual recommendations — every non-terminal, cost-reducing recommendation
 * (see `isRecoverable`), so it reconciles with the headline boxes.
 */
export function buildTree(envs: EnvRecs[]): { cats: CategoryGroup[]; total: number } {
  const catMap = new Map<
    RecommendationCategory,
    { amount: number; count: number; domains: Map<string, DomainGroup> }
  >();
  for (const env of envs) {
    for (const r of env.recs) {
      if (!isRecoverable(r)) continue;
      const cat = catMap.get(r.category) ?? { amount: 0, count: 0, domains: new Map() };
      cat.amount += r.estimatedMonthlySaving;
      cat.count += 1;
      const dom = cat.domains.get(env.domainId) ?? {
        domainId: env.domainId,
        name: env.name,
        amount: 0,
        count: 0,
        recs: [],
      };
      dom.amount += r.estimatedMonthlySaving;
      dom.count += 1;
      dom.recs.push({
        id: r.id,
        domainId: env.domainId,
        title: r.title,
        resource: r.resourceName,
        amount: r.estimatedMonthlySaving,
      });
      cat.domains.set(env.domainId, dom);
      catMap.set(r.category, cat);
    }
  }
  const cats = [...catMap.entries()]
    .map(([category, c]) => ({
      category,
      amount: c.amount,
      count: c.count,
      domains: [...c.domains.values()]
        .map((d) => ({ ...d, recs: [...d.recs].sort((a, b) => b.amount - a.amount) }))
        .sort((a, b) => b.amount - a.amount),
    }))
    .sort((a, b) => b.amount - a.amount);
  return { cats, total: cats.reduce((s, c) => s + c.amount, 0) };
}

const explanation = (
  <>
    <p>
      Where your recoverable savings sit by <b>type of waste</b> — right-sizing over-provisioned
      resources, idle/orphaned resources, storage tiering, architecture modernization, and
      networking. It tells you whether this is a &quot;turn off waste&quot; or a
      &quot;modernize&quot; problem.
    </p>
    <p>
      Sums the estimated monthly saving of every recommendation still in flight — <b>open</b>,{' '}
      <b>accepted</b>, or in validation. Already-realized (validated) or discarded (rejected)
      recommendations are excluded, as are reliability scale-ups (which raise cost). Expand a row to
      drill into the domains and the exact recommendations behind each number.
    </p>
  </>
);

/** A rotating disclosure chevron. */
function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={`shrink-0 text-muted transition-transform ${open ? 'rotate-90' : ''}`}
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

const amtPct = (amount: number, total: number) =>
  total > 0 ? Math.round((amount / total) * 100) : 0;

/** One category disclosure: the header (label + count + amount + share bar) is a
 * button that expands to the domain (org) or recommendation (env) rows. */
function CategoryRow({
  cat,
  total,
  showDomains,
}: {
  cat: CategoryGroup;
  total: number;
  showDomains: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [openDoms, setOpenDoms] = useState<Set<string>>(new Set());
  const meta = RECO_CATEGORY_META[cat.category];
  const pct = amtPct(cat.amount, total);
  const toggleDom = (id: string) =>
    setOpenDoms((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const flatRecs = showDomains ? [] : cat.domains.flatMap((d) => d.recs);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="group w-full rounded-sm text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-iris/50"
      >
        <div className="mb-1 flex items-center justify-between gap-2">
          <span className="flex min-w-0 items-center gap-1.5 text-sm text-haze">
            <Chevron open={open} />
            <CategoryIcon category={cat.category} className="text-muted" />
            <span className="truncate group-hover:text-bone">{meta.label}</span>
            <span className="shrink-0 text-[0.65rem] text-muted">
              · {cat.count} recommendation{cat.count === 1 ? '' : 's'}
            </span>
          </span>
          <span className="shrink-0 text-sm">
            <span className="font-medium text-bone tnum">{usd(cat.amount)}</span>
            <span className="ml-1 text-[0.65rem] text-muted tnum">{pct}%</span>
          </span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-surface-2">
          <div
            className="h-full rounded-full"
            style={{ width: `${Math.max(2, pct)}%`, backgroundColor: meta.color }}
          />
        </div>
      </button>

      {open && (
        <div className="ml-[6px] mt-2 divide-y divide-hairline border-l border-hairline pl-3">
          {showDomains
            ? cat.domains.map((dom) => {
                const domOpen = openDoms.has(dom.domainId);
                return (
                  <div key={dom.domainId}>
                    <button
                      type="button"
                      onClick={() => toggleDom(dom.domainId)}
                      aria-expanded={domOpen}
                      className="flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-left text-xs transition-colors hover:bg-surface-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-iris/50"
                    >
                      <span className="flex min-w-0 items-center gap-1.5 text-haze hover:text-bone">
                        <Chevron open={domOpen} />
                        <span className="truncate">{dom.name}</span>
                        <span className="shrink-0 text-[0.62rem] text-muted">
                          · {dom.count} rec{dom.count === 1 ? '' : 's'}
                        </span>
                      </span>
                      <span className="shrink-0 tnum text-muted">{usd(dom.amount)}/mo</span>
                    </button>
                    {domOpen && (
                      <ul className="ml-[6px] mb-1.5 divide-y divide-hairline border-l border-hairline pl-3">
                        {dom.recs.map((rec) => (
                          <RecItem key={rec.id} rec={rec} />
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })
            : flatRecs.map((rec) => <RecItem key={rec.id} rec={rec} />)}
        </div>
      )}
    </div>
  );
}

/** A single recommendation row — links straight to its detail page. */
function RecItem({ rec }: { rec: RecLite }) {
  return (
    <li>
      <Link
        to={`/domains/${rec.domainId}/recommendations/${rec.id}`}
        className="flex w-full items-baseline justify-between gap-2 rounded-sm px-2 py-1.5 text-xs text-haze transition-colors hover:bg-surface-2 hover:text-iris focus:outline-none focus-visible:ring-2 focus-visible:ring-iris/50"
      >
        <span className="flex min-w-0 flex-col">
          <span className="truncate">{rec.title}</span>
          <span className="truncate font-mono text-[0.6rem] text-muted">{rec.resource}</span>
        </span>
        <span className="shrink-0 self-center tnum text-muted">{usd(rec.amount)}/mo</span>
      </Link>
    </li>
  );
}

/**
 * Savings-by-category widget: at org scope it aggregates across every domain
 * with a per-domain filter; at env scope it shows just that domain. Rows expand
 * (org: category → domains → recommendations; env: category → recommendations),
 * and a recommendation links to its detail page. Sits above Cost Optimization
 * Status on both dashboards.
 */
export function SavingsByCategory(props: { scope: 'org' } | { scope: 'env'; domainId: string }) {
  const [envFilter, setEnvFilter] = useState<string>('all');
  const isOrg = props.scope === 'org';

  const org = useOrgDomainRecs();
  const envRecs = useQuery({
    queryKey: ['recommendations', isOrg ? 'none' : props.domainId],
    queryFn: () => getRecommendations((props as { domainId: string }).domainId),
    enabled: !isOrg,
    refetchInterval: 60_000,
  });

  const { loaded, cats, total, envOptions } = useMemo(() => {
    if (isOrg) {
      const scoped =
        envFilter === 'all' ? org.envs : org.envs.filter((e) => e.domainId === envFilter);
      const { cats, total } = buildTree(scoped);
      const envOptions = [
        { value: 'all', label: 'All domains' },
        ...org.envs.map((e) => ({ value: e.domainId, label: e.name })),
      ];
      return { loaded: org.loaded, cats, total, envOptions };
    }
    const single: EnvRecs[] = [
      { domainId: props.domainId, name: '', recs: envRecs.data?.items ?? [] },
    ];
    const { cats, total } = buildTree(single);
    return { loaded: !!envRecs.data, cats, total, envOptions: [] };
  }, [isOrg, org.envs, org.loaded, envFilter, envRecs.data, props]);

  // Org scope drills through domains; env scope (one domain) goes straight to recs.
  const showDomains = isOrg;

  return (
    <Card className="p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1.5">
          <h2 className="font-display text-sm font-semibold uppercase tracking-wide text-haze">
            Recoverable by category
          </h2>
          <span
            className="inline-flex items-center gap-1 rounded-full border border-hairline px-2 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wide text-muted"
            title="Estimated from each recommendation's projected monthly saving, not a billed figure."
          >
            <span className="h-1.5 w-1.5 rounded-full bg-muted" aria-hidden />
            Estimated
          </span>
          <InfoButton label="Recoverable by category">{explanation}</InfoButton>
        </div>
        {isOrg && loaded && (
          <Select
            value={envFilter}
            onChange={setEnvFilter}
            ariaLabel="Domain"
            size="sm"
            options={envOptions}
          />
        )}
      </div>

      {!loaded ? (
        <SkeletonText lines={3} lineClassName="h-4" />
      ) : cats.length === 0 ? (
        <p className="py-4 text-sm text-muted">
          No open savings opportunities right now — nothing to break down yet.
        </p>
      ) : (
        <>
          <div className="mb-3 text-sm text-muted">
            <span className="font-display text-lg font-bold text-iris">{usd(total)}</span>
            <span className="text-xs"> /mo recoverable across {cats.length} categories</span>
          </div>
          <div className="space-y-2.5">
            {cats.map((c) => (
              <CategoryRow key={c.category} cat={c} total={total} showDomains={showDomains} />
            ))}
          </div>
        </>
      )}
    </Card>
  );
}
